package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.core.auth.Session
import com.qrkontaq.data.dto.PlatformOverviewDto
import com.qrkontaq.data.dto.PlatformPaymentDto
import com.qrkontaq.data.dto.PlatformRestaurantDto
import com.qrkontaq.data.dto.WebAnalyticsDto
import com.qrkontaq.data.repository.PlatformRepository
import com.qrkontaq.data.repository.PlatformResult
import com.qrkontaq.di.LocalAppContainer
import com.qrkontaq.ui.tenant.InlineIcon
import com.qrkontaq.utils.PageSeo
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh

private sealed interface PlatformState {
    data object Booting : PlatformState

    data object NeedsLogin : PlatformState

    data object Loading : PlatformState

    data object Denied : PlatformState

    data object Error : PlatformState

    data class Ready(
        val overview: PlatformOverviewDto,
        val venues: List<PlatformRestaurantDto>,
        val payments: List<PlatformPaymentDto>,
    ) : PlatformState
}

/** Sidebar sections of the command center. */
internal enum class PlatformSection(val key: String, val icon: String) {
    OVERVIEW("OVERVIEW", AdminIcons.Dashboard),
    TRAFFIC("TRAFFIC", AdminIcons.ChartBar),
    SOURCES("SOURCES", AdminIcons.Globe),
    SESSIONS("SESSIONS", AdminIcons.Footprints),
    VENUES("VENUES", AdminIcons.ChefHat),
    PAYMENTS("PAYMENTS", AdminIcons.CreditCard),
}

private fun sectionLabel(section: PlatformSection): String =
    when (section) {
        PlatformSection.OVERVIEW -> PlatformT.navOverview
        PlatformSection.TRAFFIC -> PlatformT.navTraffic
        PlatformSection.SOURCES -> PlatformT.navSources
        PlatformSection.SESSIONS -> PlatformT.navSessions
        PlatformSection.VENUES -> PlatformT.navVenues
        PlatformSection.PAYMENTS -> PlatformT.navPayments
    }

/**
 * The platform owner's cross-venue command center. Served from the adminkontaq
 * subdomain; the backend gates every call by the global ADMIN role, the UI only
 * reflects the outcome.
 */
@Composable
fun PlatformAdminScreen() {
    PageSeo(title = "QRkontaq — Platform", description = "")
    InjectAdminStyles()
    InjectPlatformStyles()

    val container = LocalAppContainer.current

    var reloadKey by remember { mutableStateOf(0) }
    var state by remember { mutableStateOf<PlatformState>(PlatformState.Booting) }

    val repository =
        remember(Session.accessToken) {
            val token = Session.accessToken
            if (token != null) {
                container.createAuthenticatedClients(token)
                PlatformRepository(container.getAuthenticatedClient())
            } else {
                null
            }
        }

    LaunchedEffect(reloadKey) {
        if (!Session.isLoggedIn || repository == null) {
            state = PlatformState.NeedsLogin
            return@LaunchedEffect
        }
        state = PlatformState.Loading
        when (val overview = repository.overview()) {
            is PlatformResult.Unauthorized -> {
                Session.clear()
                state = PlatformState.NeedsLogin
            }

            is PlatformResult.Denied -> state = PlatformState.Denied

            is PlatformResult.Error -> state = PlatformState.Error

            is PlatformResult.Ok -> {
                val venues = (repository.restaurants() as? PlatformResult.Ok)?.value.orEmpty()
                val payments = (repository.payments() as? PlatformResult.Ok)?.value.orEmpty()
                state = PlatformState.Ready(overview.value, venues, payments)
            }
        }
    }

    when (val current = state) {
        is PlatformState.NeedsLogin ->
            LoginView { reloadKey++ }

        is PlatformState.Booting, is PlatformState.Loading ->
            PlatformShell(PlatformSection.OVERVIEW, {}) { PlatformSkeleton() }

        is PlatformState.Denied ->
            PlatformShell(PlatformSection.OVERVIEW, {}) {
                PlatformNotice(PlatformT.deniedTitle, PlatformT.deniedText, PlatformT.logout) {
                    Session.clear()
                    reloadKey++
                }
            }

        is PlatformState.Error ->
            PlatformShell(PlatformSection.OVERVIEW, {}) {
                PlatformNotice(PlatformT.title, PlatformT.errorText, PlatformT.retry) { reloadKey++ }
            }

        is PlatformState.Ready ->
            PlatformReady(
                current = current,
                repository = repository!!,
                onRefresh = { reloadKey++ },
                onLogout = {
                    Session.clear()
                    reloadKey++
                },
            )
    }
}

@Composable
private fun PlatformReady(
    current: PlatformState.Ready,
    repository: PlatformRepository,
    onRefresh: () -> Unit,
    onLogout: () -> Unit,
) {
    var section by remember {
        mutableStateOf(
            PlatformSection.entries.firstOrNull {
                it.key == com.qrkontaq.utils.SessionStorage.get("platform.section")
            } ?: PlatformSection.OVERVIEW,
        )
    }

    PlatformShell(
        active = section,
        onSelect = {
            section = it
            com.qrkontaq.utils.SessionStorage.save("platform.section", it.key)
        },
    ) {
        PlatformTopBar(sectionLabel(section), onRefresh, onLogout)
        when (section) {
            PlatformSection.OVERVIEW -> PlatformOverviewView(current.overview)
            PlatformSection.VENUES -> PlatformVenuesView(current.venues, repository, onDeleted = onRefresh)
            PlatformSection.PAYMENTS -> PlatformPaymentsView(current.payments)
            PlatformSection.TRAFFIC, PlatformSection.SOURCES, PlatformSection.SESSIONS ->
                AnalyticsScaffold(repository, section)
        }
    }
}

/**
 * Owns the analytics period/site filters plus one shared WebAnalyticsDto for the
 * traffic/sources/sessions sections, refetching on filter change and quietly
 * every 60s.
 */
@Composable
private fun AnalyticsScaffold(
    repository: PlatformRepository,
    section: PlatformSection,
) {
    var days by remember { mutableStateOf(30) }
    var site by remember { mutableStateOf<String?>(null) }
    var data by remember { mutableStateOf<WebAnalyticsDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var failed by remember { mutableStateOf(false) }

    LaunchedEffect(days, site) {
        loading = data == null
        failed = false
        when (val result = repository.webAnalytics(days, site)) {
            is PlatformResult.Ok -> data = result.value
            else -> if (data == null) failed = true
        }
        loading = false
        // Quiet auto-refresh: refetch in place without flipping the skeleton.
        while (true) {
            delay(60_000)
            (repository.webAnalytics(days, site) as? PlatformResult.Ok)?.let { data = it.value }
        }
    }

    // Filter bar
    Row(
        Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(8.px).margin(bottom = 16.px),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        QpChip(PlatformT.period7, days == 7) { days = 7 }
        QpChip(PlatformT.period30, days == 30) { days = 30 }
        QpChip(PlatformT.period90, days == 90) { days = 90 }
        Box(Modifier.width(12.px))
        QpChip(PlatformT.siteAll, site == null) { site = null }
        QpChip(PlatformT.siteLanding, site == "LANDING") { site = "LANDING" }
        QpChip(PlatformT.siteAdmin, site == "ADMIN") { site = "ADMIN" }
        QpChip(PlatformT.sitePlatform, site == "PLATFORM") { site = "PLATFORM" }
    }

    val current = data
    when {
        loading && current == null -> PlatformSkeleton()
        failed && current == null ->
            QpCard { SpanText(PlatformT.errorText, Modifier.fontSize(13.px).color(Color.rgba(242, 247, 244, 0.6f))) }
        current != null ->
            when (section) {
                PlatformSection.TRAFFIC -> PlatformTrafficView(current, days)
                PlatformSection.SOURCES -> PlatformSourcesView(current)
                PlatformSection.SESSIONS -> PlatformSessionsView(repository, current, days, site)
                else -> {}
            }
    }
}

/** Fixed sidebar + main content column. */
@Composable
private fun PlatformShell(
    active: PlatformSection,
    onSelect: (PlatformSection) -> Unit,
    content: @Composable () -> Unit,
) {
    Box(Modifier.fillMaxWidth().minHeight(100.vh).attrsModifier { classes("qp-root") }) {
        Box(Modifier.attrsModifier { classes("qp-scene") }) {
            Box(Modifier.attrsModifier { classes("qp-grid") })
            org.jetbrains.compose.web.dom.I({})
            org.jetbrains.compose.web.dom.I({})
            org.jetbrains.compose.web.dom.I({})
        }
        Box(Modifier.attrsModifier { classes("qp-noise") })
        Row(Modifier.fillMaxWidth().attrsModifier { classes("qp-shell") }) {
            PlatformSidebar(active, onSelect)
            Box(Modifier.attrsModifier { classes("qp-main") }.styleModifier { property("flex", "1"); property("min-width", "0") }) {
                Column(Modifier.fillMaxWidth()) { content() }
            }
        }
    }
}

@Composable
private fun PlatformSidebar(
    active: PlatformSection,
    onSelect: (PlatformSection) -> Unit,
) {
    Column(Modifier.attrsModifier { classes("qp-side") }) {
        Box(Modifier.attrsModifier { classes("qp-side-logo") }) { SpanText("Q") }
        PlatformSection.entries.forEach { s ->
            Box(
                Modifier
                    .attrsModifier {
                        classes(*listOfNotNull("qp-side-item", "on".takeIf { s == active }).toTypedArray())
                        attr("role", "button")
                        attr("aria-label", sectionLabel(s))
                    }
                    .onClick { onSelect(s) },
                contentAlignment = Alignment.Center,
            ) {
                InlineIcon(s.icon, 20, "currentColor")
                Box(Modifier.attrsModifier { classes("qp-tip") }) { SpanText(sectionLabel(s)) }
            }
        }
        Box(Modifier.attrsModifier { classes("qp-side-spring") })
    }
}

@Composable
private fun PlatformTopBar(
    title: String,
    onRefresh: () -> Unit,
    onLogout: () -> Unit,
) {
    Row(
        Modifier.fillMaxWidth().margin(bottom = 18.px).flexWrap(FlexWrap.Wrap).gap(12.px),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Column {
            SpanText(
                title,
                Modifier.fontSize(26.px).fontWeight(FontWeight.Bold).color(Color.rgb(0xF2, 0xF7, 0xF4))
                    .attrsModifier { classes("qp-display") },
            )
            SpanText(PlatformT.subtitle, Modifier.fontSize(12.5.px).color(Color.rgba(242, 247, 244, 0.5f)))
        }
        Row(Modifier.gap(8.px), verticalAlignment = Alignment.CenterVertically) {
            AdminLanguage.entries.forEach { language ->
                val on = language == adminLanguage
                Box(
                    Modifier
                        .cursor(Cursor.Pointer)
                        .attrsModifier { classes(*listOfNotNull("qp-chip", "on".takeIf { on }).toTypedArray()) }
                        .onClick { setAdminLanguage(language) },
                ) { SpanText(language.shortLabel) }
            }
            IconButton(AdminIcons.QrCode, PlatformT.refresh, onRefresh)
            IconButton(AdminIcons.LogOut, PlatformT.logout, onLogout)
        }
    }
}

@Composable
private fun IconButton(
    icon: String,
    label: String,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .size(38.px)
            .borderRadius(11.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.05f))
            .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, Color.rgba(255, 255, 255, 0.1f))
            .cursor(Cursor.Pointer)
            .attrsModifier { classes("qp-chip"); attr("role", "button"); attr("aria-label", label) }
            .onClick { onClick() },
        contentAlignment = Alignment.Center,
    ) {
        InlineIcon(icon, 16, "rgba(242,247,244,.75)")
    }
}

@Composable
private fun PlatformSkeleton() {
    Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(12.px).margin(bottom = 14.px)) {
        repeat(4) {
            Box(
                Modifier.height(74.px)
                    .styleModifier { property("flex", "1"); property("min-width", "158px") }
                    .attrsModifier { classes("qp-skeleton") },
            )
        }
    }
    Box(Modifier.fillMaxWidth().height(240.px).attrsModifier { classes("qp-skeleton") })
}

@Composable
private fun PlatformNotice(
    title: String,
    text: String,
    action: String,
    onAction: () -> Unit,
) {
    QpCard(Modifier.margin(top = 60.px).maxWidth(440.px)) {
        SpanText(
            title,
            Modifier.fontSize(18.px).fontWeight(FontWeight.Bold).color(Color.rgb(0xF2, 0xF7, 0xF4)).margin(bottom = 8.px),
        )
        SpanText(text, Modifier.fontSize(13.5.px).color(Color.rgba(242, 247, 244, 0.6f)).margin(bottom = 16.px))
        Box(
            Modifier
                .padding(topBottom = 10.px, leftRight = 18.px)
                .borderRadius(12.px)
                .backgroundColor(Color.rgb(0x0F, 0x9D, 0x6E))
                .cursor(Cursor.Pointer)
                .attrsModifier { classes("qp-chip"); attr("role", "button") }
                .onClick { onAction() },
        ) {
            SpanText(action, Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold).color(Color.rgb(255, 255, 255)))
        }
    }
}
